/**
 * Act II contracts and stands (MGT-015/016, alpha). Each gate has a Small/Medium/Large stand; an airline contract puts
 * one airline on one compatible gate for a three-shift term: its aircraft size sets the seats, each departure pays its
 * fee once, and meeting the on-time target pays a bonus at term end (never a fine). Offers are derived from the shift
 * number, so only signed contracts and stands are stored.
 */
import { AIRPORTS, tier } from './airports.js';
import type { EconomyData } from './types.js';

export const STAND_SEATS = [6, 14, 24] as const;
export const STAND_NAMES = ['Small', 'Medium', 'Large'] as const;
export const TIERS = [
    { name: 'Friendly', fee: 0.7, target: 60 },
    { name: 'Standard', fee: 1, target: 75 },
    { name: 'Premium', fee: 1.4, target: 90 },
] as const;
export const TERM_SHIFTS = 3;
const AIRLINES = ['Bluebird Air', 'Pinecone Express', 'Harbor Hopper', 'Gull Coast', 'Northstar', 'Tidewater', 'Sunline', 'Penrose Air', 'Maple Wings', 'Starla'];
const LIMITS: Record<string, number> = { BVM: 4, MTR: 6 };
const STARTING_STANDS: Record<string, number[]> = { BVM: [0, 0, 1], MTR: [2, 2, 1, 1] };

export interface Contract {
    airline: string; gate: number; tier: number; stand: number; fee: number; target: number;
    international: boolean; premium: boolean; minStars: number; termLeft: number; departures: number; onTime: number;
}
export interface AirlineState { stands: number[]; contracts: Contract[]; renew: string[] }
export type Offer = Omit<Contract, 'gate' | 'termLeft' | 'departures' | 'onTime'>;

/** Contracts exist at the Act II airports (Bayview and Metro). */
export function contractAirport(e: Pick<EconomyData, 'run'>): boolean { return tier(e).code in LIMITS; }
export function contractLimit(e: Pick<EconomyData, 'run'>): number { return Math.min(LIMITS[tier(e).code] ?? 0, tier(e).maxGates); }
export function freshAirline(e: Pick<EconomyData, 'run'>): AirlineState {
    const port = tier(e), start = STARTING_STANDS[port.code] ?? [];
    return { stands: Array.from({ length: port.maxGates }, (_, i) => start[i] ?? 1), contracts: [], renew: [] };
}
export function readAirline(json: string, e: Pick<EconomyData, 'run'>): AirlineState {
    if (!json) return freshAirline(e);
    const parsed = validateAirline(JSON.parse(json), tier(e).maxGates);
    return parsed ?? freshAirline(e);
}
const int = (v: unknown, max: number, min = 0) => Number.isSafeInteger(v) && (v as number) >= min && (v as number) <= max;
/** Total validation of a stored airline block. */
export function validateAirline(raw: unknown, gates: number): AirlineState | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const r = raw as Record<string, any>;
    if (Object.keys(r).sort().join() !== 'contracts,renew,stands' || !Array.isArray(r.stands) || r.stands.length !== gates || !r.stands.every((s: unknown) => int(s, 2))) return null;
    if (!Array.isArray(r.renew) || r.renew.length > 8 || !r.renew.every((a: unknown) => typeof a === 'string' && AIRLINES.includes(a))) return null;
    if (!Array.isArray(r.contracts) || r.contracts.length > gates) return null;
    const keys = 'airline,departures,fee,gate,international,minStars,onTime,premium,stand,target,termLeft,tier';
    const used = new Set<number>(), contracts: Contract[] = [];
    for (const c of r.contracts) {
        if (!c || typeof c !== 'object' || Object.keys(c).sort().join() !== keys || !AIRLINES.includes(c.airline) || !int(c.gate, gates - 1) || used.has(c.gate)
            || !int(c.tier, 2) || !int(c.stand, 2) || c.stand > r.stands[c.gate] || !int(c.fee, 1e9, 1) || !int(c.target, 100) || typeof c.international !== 'boolean'
            || typeof c.premium !== 'boolean' || !int(c.minStars, 50) || !int(c.termLeft, TERM_SHIFTS, 1) || !int(c.departures, 1e9) || !int(c.onTime, c.departures)) return null;
        used.add(c.gate);
        contracts.push({ airline: c.airline, gate: c.gate, tier: c.tier, stand: c.stand, fee: c.fee, target: c.target, international: c.international, premium: c.premium,
            minStars: c.minStars, termLeft: c.termLeft, departures: c.departures, onTime: c.onTime });
    }
    return { stands: [...r.stands], contracts, renew: [...r.renew] };
}

const mix = (a: number, b: number) => { let h = (a * 2654435761 ^ b * 40503) >>> 0; h ^= h >>> 15; h = Math.imul(h, 2246822519) >>> 0; return h; };
/** Three offers for this shift, deterministic from the airport and shift number; expired contracts come back as renewals first. */
export function offers(e: Pick<EconomyData, 'run'>, shift: number, airline: AirlineState): Offer[] {
    if (!contractAirport(e)) return [];
    const port = tier(e), metro = port.code === 'MTR', taken = new Set(airline.contracts.map(c => c.airline));
    const names = [...airline.renew.filter(a => !taken.has(a)), ...AIRLINES.filter(a => !taken.has(a) && !airline.renew.includes(a))];
    return names.slice(0, 3).map((name, n) => {
        const h = mix(shift * 7 + AIRPORTS.indexOf(port), AIRLINES.indexOf(name) + n);
        const stand = h % 3, tierIndex = (h >>> 3) % 3, international = metro && (h >>> 6) % 10 < 4, premium = (h >>> 10) % 10 < 3;
        const fee = Math.round(STAND_SEATS[stand] * 12 * port.incomeMult * TIERS[tierIndex].fee * (international ? 1.3 : 1));
        return { airline: name, tier: tierIndex, stand, fee, target: TIERS[tierIndex].target, international, premium, minStars: premium ? 30 : tierIndex === 2 ? 25 : 0 };
    });
}
/** Why an offer cannot be signed at a gate now, or null when it can. */
export function signRefusal(o: Offer, gate: number, airline: AirlineState, e: Pick<EconomyData, 'run'> & { starsOverall: number }, passportOpen: boolean, loungeStaffed: boolean): string | null {
    if (!contractAirport(e)) return 'No airline contracts here.';
    if (airline.contracts.length >= contractLimit(e)) return `This airport supports ${contractLimit(e)} contracts.`;
    if (!Number.isInteger(gate) || gate < 0 || gate >= airline.stands.length) return 'Choose a gate.';
    if (airline.contracts.some(c => c.gate === gate)) return 'That gate already has an airline.';
    if (airline.stands[gate] < o.stand) return `Needs a ${STAND_NAMES[o.stand]} stand.`;
    if (e.starsOverall < o.minStars) return `Needs ${(o.minStars / 10).toFixed(1)}★.`;
    if (o.international && !passportOpen) return 'Needs Passport Control.';
    if (o.premium && !loungeStaffed) return 'Needs a staffed Sky Lounge.';
    return null;
}
export function standUpgradeCost(stand: number, e: Pick<EconomyData, 'run'>): number {
    return stand >= 2 ? Infinity : Math.ceil(1500 * (stand + 1) * tier(e).costMult);
}
